import React, { Fragment, useEffect, useState } from 'react';
import { AppActions, AppTranslationHelper, Item, User } from 'common/types';
import { ClosedOrder } from 'common/types';
import { CommonLayout } from '../index';
import { ChevronDownTwoTone, ChevronUpTwoTone, SearchTwoTone, CalendarRangeTwoTone, AccountDetailsTwoTone, CardOutlineTwoTone, CloseTwoTone, TrashCanOutlineTwoTone } from 'common/icons';
import styles from './orders.module.css';

// Extendemos la interfaz ClosedOrder para incluir propiedades de cancelación
interface ExtendedClosedOrder extends ClosedOrder {
  isCancelled?: boolean;
  cancelledBy?: string;
  cancelledAt?: number;
  cancellationReason?: string;
}

// Extendemos la interfaz OrdersActions para incluir el método cancelOrder
declare module 'common/types' {
  interface OrdersActions {
    cancelOrder(orderId: string, reason: string): Promise<void>;
  }
}

type OrdersListProps = {
  actions: AppActions;
  orders: ClosedOrder[];
  items: Item[];
  translation: AppTranslationHelper;
  context?: any; // To access users and isAdmin
};

const OrdersList: React.FC<OrdersListProps> = ({ orders, actions, translation, items, context }) => {
  const { t, formatDate, formatFinancial } = translation;
  const [expandedRows, setExpandedRows] = useState<number[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOrders, setFilteredOrders] = useState<ExtendedClosedOrder[]>([]);
  const [orderType, setOrderType] = useState<string>('all');
  
  // Estados para la cancelación de órdenes
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [orderToCancel, setOrderToCancel] = useState<ExtendedClosedOrder | null>(null);
  const [cancellationReason, setCancellationReason] = useState('');
  const [isCancelling, setIsCancelling] = useState(false);
  const [cancelError, setCancelError] = useState('');

  
  // Verificar si el usuario es administrador
  const isAdmin = context?.isAdmin || false;
  const users = context?.users || [];
  
  // Set today's date as default value for startDate
  // and tomorrow's date as default value for endDate
  const today = new Date().toISOString().split('T')[0];
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const tomorrowStr = tomorrow.toISOString().split('T')[0];
  
  const [startDate, setStartDate] = useState<string>(today);
  const [endDate, setEndDate] = useState<string>(tomorrowStr);
  
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [paymentType, setPaymentType] = useState<string>('all');
  const [showCancelled, setShowCancelled] = useState<boolean>(false);

  // Filter orders when search term, dates, user or orders change
  useEffect(() => {
    // Aseguramos que todas las órdenes tengan el tipo extendido con propiedades de cancelación
    let filtered = orders as ExtendedClosedOrder[];
    
    // Ordenar las órdenes por fecha de manera descendente (más recientes primero)
    filtered.sort((a, b) => b.dateClose - a.dateClose);
    
    // Filtrar órdenes canceladas si showCancelled es false
    if (!showCancelled) {
      filtered = filtered.filter(order => !order.isCancelled);
    }
    
    // Filter by search term
    if (searchTerm.trim() !== '') {
      const lowercaseSearch = searchTerm.toLowerCase();
      filtered = filtered.filter(order => {
        // Search in ID
       
        if (order.orderName.toString().toLowerCase().includes(lowercaseSearch)) return true;
        
        // Search in items
        // const hasMatchingItem = order.items.some(item => {
        //   const itemName = getItemName(item.id);
        //   return itemName.toLowerCase().includes(lowercaseSearch);
        // });
        
        // return hasMatchingItem;
        return false
      });
    }
    
    // Filter by start date
    if (startDate) {
      const startTimestamp = new Date(startDate).setHours(0, 0, 0, 0);
      filtered = filtered.filter(order => order.dateClose >= startTimestamp);
    }
    
    // Filter by end date
    if (endDate) {
      const endTimestamp = new Date(endDate).setHours(23, 59, 59, 999);
      filtered = filtered.filter(order => order.dateClose <= endTimestamp);
    }
    
    // Filter by user
    if (selectedUserId) {
      filtered = filtered.filter(order => order.ownerId === selectedUserId);
    }
    
    // Filter by payment type
    if (paymentType !== 'all') {
      filtered = filtered.filter(order => {
        // Detectar pagos en efectivo y tarjeta
        const hasCashPayment = order.cashPaymentAmount > 0;
        const hasCardPayment = order.cardPaymentAmount > 0;
        
        // Calcular el total de pagos en efectivo y tarjeta
        const cashAndCardTotal = order.cashPaymentAmount + order.cardPaymentAmount;
        
        // Detectar si hay transferencia bancaria (por diferencia o por paymentMethodId)
        const hasTransferByDifference = order.totalPaymentAmount > cashAndCardTotal + 0.01;
        const hasTransferByMethod = order.paymentMethodId === 3;
        const hasTransferPayment = hasTransferByDifference || hasTransferByMethod;
        
        // Determinar si es un pago mixto basado en las combinaciones
        const isMixedPayment = (hasCashPayment && hasCardPayment) || 
                              (hasCashPayment && hasTransferPayment) || 
                              (hasCardPayment && hasTransferPayment);
        
        if (paymentType === 'cash') return hasCashPayment && !hasCardPayment && !hasTransferPayment;
        if (paymentType === 'card') return hasCardPayment && !hasCashPayment && !hasTransferPayment;
        if (paymentType === 'transfer') return hasTransferPayment && !hasCashPayment && !hasCardPayment;
        if (paymentType === 'mixed') return isMixedPayment;
        
        return false;
      });
    }
    
    // Filter by order type (external or internal)
    if (orderType !== 'all') {
      filtered = filtered.filter(order => {
        if (orderType === 'external') return order.isExternal === true;
        if (orderType === 'internal') return !order.isExternal;
        return true;
      });
    }
    
    setFilteredOrders(filtered);
  }, [searchTerm, orders, items, startDate, endDate, selectedUserId, paymentType, orderType, showCancelled]);

  // Function to handle click on a row
  const handleRowClick = (rowId: number) => {
    setExpandedRows(prev => 
      prev.includes(rowId) 
        ? prev.filter(id => id !== rowId) 
        : [...prev, rowId]
    );
  };

  const getItemName = (idItem: string) => {
    const item = items.find(item => item.id === idItem);
    return item ? item.name : t('admin.orders.productNotFound', 'Product not found');
  };

  const formatOrderDate = (timestamp: number) => {
    const date = new Date(timestamp);
    return formatDate(date, 'PPpp'); // Format: April 20, 2025, 11:22:34 PM
  };

  const getUserName = (order : ClosedOrder) => {
    if (!order.ownerId) return t('admin.orders.userNotSpecified', 'Usuario no especificado');
    const user = users.find((u: User) => u.id === order.ownerId);
    return user ? user.displayName : t('admin.orders.unknownUser', 'Usuario desconocido');
  };

  // Determinar el tipo de pago de una orden
  const getPaymentType = (order: ClosedOrder) => {
    // Detectar pagos en efectivo y tarjeta
    const hasCashPayment = order.cashPaymentAmount > 0;
    const hasCardPayment = order.cardPaymentAmount > 0;
    
    // Calcular el total de pagos en efectivo y tarjeta
    const cashAndCardTotal = order.cashPaymentAmount + order.cardPaymentAmount;
    
    // Detectar si hay transferencia bancaria (por diferencia o por paymentMethodId)
    const hasTransferByDifference = order.totalPaymentAmount > cashAndCardTotal + 0.01;
    const hasTransferByMethod = order.paymentMethodId === 3;
    const hasTransferPayment = hasTransferByDifference || hasTransferByMethod;
    
    // Determinar el tipo de pago basado en las combinaciones
    if (hasCashPayment && hasCardPayment && hasTransferPayment) {
      return t('admin.orders.paymentTypeMixed', 'Mixto'); // Efectivo + Tarjeta + Transferencia
    } else if (hasCashPayment && hasCardPayment) {
      return t('admin.orders.paymentTypeMixed', 'Mixto'); // Efectivo + Tarjeta
    } else if (hasCashPayment && hasTransferPayment) {
      return t('admin.orders.paymentTypeMixed', 'Mixto'); // Efectivo + Transferencia
    } else if (hasCardPayment && hasTransferPayment) {
      return t('admin.orders.paymentTypeMixed', 'Mixto'); // Tarjeta + Transferencia
    } else if (hasCashPayment) {
      return t('admin.orders.paymentTypeCash', 'Efectivo');
    } else if (hasCardPayment) {
      return t('admin.orders.paymentTypeCard', 'Tarjeta');
    } else if (hasTransferPayment) {
      return t('admin.orders.paymentTypeTransfer', 'Transferencia');
    } else {
      return t('admin.orders.paymentTypeUnknown', 'Desconocido');
    }
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setStartDate(today);
    setEndDate(tomorrowStr);
    setSelectedUserId('');
    setPaymentType('all');
    setOrderType('all');
  };

  // Función para manejar la cancelación de una orden
  const handleCancelOrder = (order: ExtendedClosedOrder, e: React.MouseEvent) => {
    e.stopPropagation(); // Evitar que se expanda la fila
    setOrderToCancel(order);
    setShowCancelModal(true);
    setCancellationReason('');
    setCancelError('');
  };
  
  // Función para cerrar el modal de cancelación
  const handleCloseCancelModal = () => {
    setShowCancelModal(false);
    setOrderToCancel(null);
    setCancellationReason('');
    setCancelError('');
  };
  
  // Función para procesar la cancelación de la orden
  const handleConfirmCancel = async () => {
    if (!orderToCancel) return;
    if (!cancellationReason.trim()) {
      setCancelError(t('admin.orders.enterCancelReason'));
      return;
    }
    
    setIsCancelling(true);
    setCancelError('');
    
    try {
      await actions.orders.cancelOrder(orderToCancel.id, cancellationReason);
      setShowCancelModal(false);
      setOrderToCancel(null);
      setCancellationReason('');
      
      // Mostrar mensaje de éxito con alert (podría mejorarse con un sistema de notificaciones)
      alert(t('admin.orders.orderCancelledSuccess'));
    } catch (error: any) {
      setCancelError(error.message || t('common.somethingWentWrong'));
    } finally {
      setIsCancelling(false);
    }
  };
  
  // Función para formatear la fecha de cancelación
  const formatCancelDate = (timestamp: number) => {
    return formatDate(timestamp, 'dd/MM/yyyy HH:mm');
  };

  const renderHead = (
    <div className={styles.header}>
      <div className={styles.title}>{t('admin.orders.title', 'Ventas')}</div>
      
      <div className={styles.controlsContainer}>
        <div className={styles.searchRow}>
          <div className={styles.searchContainer}>
            <SearchTwoTone className={styles.searchIcon} />
            <input 
              type="text" 
              className={styles.searchInput} 
              placeholder={t('admin.orders.searchPlaceholder', 'Buscar ventas...')} 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button 
                className={styles.clearButton} 
                onClick={() => setSearchTerm('')}
              >
                ×
              </button>
            )}
          </div>
          
          <div className={styles.actionsContainer}>
            {(searchTerm || startDate !== today || endDate !== tomorrowStr || selectedUserId || paymentType !== 'all' || orderType !== 'all') && (
              <button className={styles.clearFiltersButton} onClick={handleClearFilters}>
                <span>{t('admin.orders.clearFilters', 'Limpiar filtros')}</span>
              </button>
            )}
            <button className={styles.refreshButton} onClick={() => window.location.reload()}>
              <span>{t('admin.orders.refresh', 'Actualizar')}</span>
            </button>
          </div>
        </div>
        
        <div className={styles.filtersRow}>
          <div className={styles.dateFilters}>
            <div className={styles.filterItem}>
              <label htmlFor="startDate" className={styles.filterLabel}>
                <CalendarRangeTwoTone className={styles.filterIcon} />
                {t('admin.orders.dateFrom', 'Desde:')}
              </label>
              <input 
                type="date" 
                id="startDate"
                className={styles.dateInput} 
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            
            <div className={styles.filterItem}>
              <label htmlFor="endDate" className={styles.filterLabel}>
                <CalendarRangeTwoTone className={styles.filterIcon} />
                {t('admin.orders.dateTo', 'Hasta:')}
              </label>
              <input 
                type="date" 
                id="endDate"
                className={styles.dateInput} 
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </div>
          
          <div className={styles.filterItem}>
            <label htmlFor="userFilter" className={styles.filterLabel}>
              <AccountDetailsTwoTone className={styles.filterIcon} />
              {t('admin.orders.user', 'Usuario')}:
            </label>
            <select
              id="userFilter"
              className={styles.userSelect}
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
            >
              <option value="">{t('admin.orders.allUsers', 'Todos los usuarios')}</option>
              {users.map((user: User) => (
                <option key={user.id} value={user.id}>
                  {user.displayName}
                </option>
              ))}
            </select>
          </div>
          
          <div className={styles.filterItem}>
            <label htmlFor="paymentTypeFilter" className={styles.filterLabel}>
              <CardOutlineTwoTone className={styles.filterIcon} />
              {t('admin.orders.paymentType', 'Tipo de pago')}:
            </label>
            <select
              id="paymentTypeFilter"
              className={styles.paymentTypeSelect}
              value={paymentType}
              onChange={(e) => setPaymentType(e.target.value)}
            >
              <option value="all">{t('admin.orders.allPayments', 'Todos los pagos')}</option>
              <option value="cash">{t('admin.orders.cash', 'Efectivo')}</option>
              <option value="card">{t('admin.orders.card', 'Tarjeta')}</option>
              <option value="transfer">{t('admin.orders.transfer', 'Transferencia')}</option>
              <option value="mixed">{t('admin.orders.mixed', 'Mixto')}</option>
            </select>
          </div>
          
          <div className={styles.filterItem}>
            <label htmlFor="orderTypeFilter" className={styles.filterLabel}>
              <SearchTwoTone className={styles.filterIcon} />
              {t('admin.orders.orderType', 'Tipo de orden')}:
            </label>
            <select
              id="orderTypeFilter"
              className={styles.paymentTypeSelect}
              value={orderType}
              onChange={(e) => setOrderType(e.target.value)}
            >
              <option value="all">{t('admin.orders.allOrders', 'Todas las órdenes')}</option>
              <option value="external">{t('admin.orders.externalOrders', 'Órdenes externas')}</option>
              <option value="internal">{t('admin.orders.internalOrders', 'Órdenes internas')}</option>
            </select>
          </div>
          
          <div className={styles.filterItem}>
            <label className={styles.filterLabel}>
              <input
                type="checkbox"
                checked={showCancelled}
                onChange={(e) => setShowCancelled(e.target.checked)}
                className={styles.checkboxInput}
              />
              {t('admin.orders.showCancelled', 'Mostrar canceladas')}
            </label>
          </div>
        </div>
      </div>
    </div>
  );

  const renderBody = (
    <div className={styles.ordersContainer}>
      {filteredOrders.length > 0 ? (
        <div className={styles.tableWrapper}>
          <table className={styles.ordersTable}>
            <thead>
              <tr>
                <th className={styles.orderNumber}>#</th>
                <th className={styles.orderId}>{t('admin.orders.id', 'ID')}</th>
                <th className={styles.orderAmount}>{t('admin.orders.amount', 'Monto')}</th>
                <th className={styles.orderDate}>{t('admin.orders.date', 'Fecha')}</th>
                <th className={styles.orderPaymentType}>{t('admin.orders.paymentType', 'Tipo de pago')}</th>
                <th className={styles.orderUser}>{t('admin.orders.user', 'Usuario')}</th>
                <th className={styles.orderActions}>{t('admin.orders.actions', 'Acciones')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.map((order, index) => (
                <Fragment key={order.id}>
                  <tr 
                    key={index} 
                    className={`
                      ${styles.orderRow} 
                      ${expandedRows.includes(index) ? styles.expanded : ''} 
                      ${order.isCancelled ? styles.cancelled : ''}
                    `} 
                    onClick={() => handleRowClick(index)}
                  >
                    <td className={styles.orderNumber}>{index + 1}</td>
                    <td className={styles.orderId}>
                      <div className={styles.orderIdCell}>
                        <span className={styles.orderName}>#{order.orderName}</span>
                        {order.isCancelled && (
                          <span className={styles.cancelBadge}>
                            {t('admin.orders.cancelled', 'CANC')}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className={styles.orderAmount}>{formatFinancial(order.totalAmount)}</td>
                    <td className={styles.orderDate}>{formatOrderDate(order.dateClose)}</td>
                    <td className={styles.orderPaymentType}>{getPaymentType(order)}</td>
                    <td className={styles.orderUser}>{getUserName(order)}</td>
                    <td className={styles.actions}>
                      <div className={styles.actionsCell}>
                        {   /* Botón de cancelar para administradores y órdenes no canceladas */}
                        {context?.isAdmin && !order.isCancelled && (
                          <button 
                            className="btn btn-link"
                            onClick={(e) => handleCancelOrder(order, e)}
                            aria-label={t('admin.orders.cancelOrder')}
                            title={t('admin.orders.cancelOrder')}
                          >
                            <TrashCanOutlineTwoTone className={styles.trashIcon} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  <tr className={`${styles.detailsRow} ${expandedRows.includes(index) ? styles.visible : ''}`}>
                    <td colSpan={7}>
                      <div className={styles.orderDetails}>
                        <div className={styles.orderItems}>
                          <h4 className={styles.detailsTitle}>{t('admin.orders.items', 'Productos')}</h4>
                          <div className={styles.itemsList}>
                            {order.items.map((item, itemIndex) => (
                               <div key={itemIndex} className={styles.orderItem}>
                                <span className={styles.itemName}>{getItemName(item.id)}</span>
                                <span className={styles.itemQuantity}>x {item.quantity}</span>
                                <span className={styles.itemPrice}>{formatFinancial(item.price)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                        
                        <div className={styles.orderSummary}>
                          {order.isExternal && order.external && (
                            <div className={styles.summarySection}>
                              <h4 className={styles.detailsTitle}>{t('admin.orders.externalOrderDetails', 'Detalles de orden externa')}</h4>
                              <div className={styles.externalOrderDetails}>
                                {order.external?.name && (
                                  <div className={styles.externalDetail}>
                                    <span>{t('admin.orders.customerName', 'Nombre')}:</span>
                                    <span>{order.external.name}</span>
                                  </div>
                                )}
                                {order.external?.address && (
                                  <div className={styles.externalDetail}>
                                    <span>{t('admin.orders.customerAddress', 'Dirección')}:</span>
                                    <span>{order.external.address}</span>
                                  </div>
                                )}
                                {order.external?.phone && (
                                  <div className={styles.externalDetail}>
                                    <span>{t('admin.orders.customerPhone', 'Teléfono')}:</span>
                                    <span>{order.external.phone}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                          
                          <div className={styles.summarySection}>
                            <h4 className={styles.detailsTitle}>{t('admin.orders.paymentDetails', 'Detalles de pago')}</h4>
                            <div className={styles.paymentMethod}>
                              {order.cashPaymentAmount > 0 && (
                                <div className={styles.paymentItem}>
                                  <span>{t('admin.orders.cash', 'Efectivo')}:</span>
                                  <span>{formatFinancial(order.cashPaymentAmount)}</span>
                                </div>
                              )}
                              {order.cardPaymentAmount > 0 && (
                                <div className={styles.paymentItem}>
                                  <span>{t('admin.orders.card', 'Tarjeta')}:</span>
                                  <span>{formatFinancial(order.cardPaymentAmount)}</span>
                                </div>
                              )}
                              {/* Mostrar el monto de transferencia bancaria si existe */}
                              {(() => {
                                // Calcular el monto de transferencia (total - efectivo - tarjeta)
                                const cashAndCardTotal = order.cashPaymentAmount + order.cardPaymentAmount;
                                const transferAmount = order.totalPaymentAmount - cashAndCardTotal;
                                
                                // Mostrar solo si hay un monto significativo de transferencia (mayor a 0.01)
                                return transferAmount > 0.01 && (
                                  <div className={styles.paymentItem}>
                                    <span>{t('admin.orders.transfer', 'Transferencia')}:</span>
                                    <span>{formatFinancial(transferAmount)}</span>
                                  </div>
                                );
                              })()}
                              <div className={styles.paymentTotal}>
                                <span>{t('admin.orders.total', 'Total')}:</span>
                                <span>{formatFinancial(order.totalAmount)}</span>
                              </div>
                            </div>
                          </div>
                          

                          
                          {/* Notas de la orden */}
                          {order.notes && (
                            <div className={styles.summarySection}>
                              <h4 className={styles.detailsTitle}>{t('admin.orders.notes', 'Notas')}</h4>
                              <div className={styles.orderNotes}>{order.notes}</div>
                            </div>
                          )}
                          
                          {/* Mostrar información de cancelación si la orden está cancelada */}
                          {order.isCancelled && (
                            <div className={styles.summarySection}>
                              <h4 className={styles.detailsTitle}>{t('admin.orders.cancellationDetails')}</h4>
                              <div className={styles.cancellationDetails}>
                                <div className={styles.cancellationItem}>
                                  <span>{t('admin.orders.cancelledBy')}:</span>
                                  <span>
                                    {order.cancelledBy ? 
                                      (users.find((u: User) => u.id === order.cancelledBy)?.email || t('admin.orders.unknownUser')) : 
                                      t('admin.orders.unknownUser')}
                                  </span>
                                </div>
                                <div className={styles.cancellationItem}>
                                  <span>{t('admin.orders.cancelledAt')}:</span>
                                  <span>{order.cancelledAt ? formatCancelDate(order.cancelledAt) : ''}</span>
                                </div>
                                <div className={styles.cancellationItem}>
                                  <span>{t('admin.orders.cancellationReason')}:</span>
                                  <span>{order.cancellationReason || ''}</span>
                                </div>
                                <div className={styles.cancellationBadge}>
                                  {t('admin.orders.cancelled')}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className={styles.empty}>
          <div className={styles.emptyIcon}>🔍</div>
          <h3>{t('admin.orders.noOrdersFound', 'No se encontraron ventas')}</h3>
          <p>
            {searchTerm || startDate !== today || endDate !== tomorrowStr || selectedUserId || paymentType !== 'all' || orderType !== 'all' ? 
              t('admin.orders.tryAnotherSearch', 'Intenta con otros filtros') : 
              t('admin.orders.noOrders', 'No hay ventas disponibles')}
          </p>
        </div>
      )}
    </div>
  );

  // Modal de confirmación para cancelar órdenes
  const renderCancelModal = (
    <div className={`${styles.modalOverlay} ${showCancelModal ? styles.show : ''}`}>
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <h3>{t('admin.orders.cancelOrderConfirm', 'Confirmar Cancelación')}</h3>
          <button className={styles.closeButton} onClick={handleCloseCancelModal}>
            ✕
          </button>
        </div>
        
        <div className={styles.modalBody}>
          <p>{t('admin.orders.cancelOrderMessage', '¿Estás seguro que deseas cancelar esta orden? Esta acción no se puede deshacer y restaurará el inventario.')}</p>
          
          {orderToCancel && (
            <div className={styles.orderInfoSimple}>
              <div>
                <span className={styles.orderInfoLabel}>{t('admin.orders.orderNumber', 'Número de Orden')}:</span> 
                <span>#{orderToCancel.orderName}</span>
              </div>
              <div>
                <span className={styles.orderInfoLabel}>{t('admin.orders.amount', 'Monto')}:</span> 
                <span>{formatFinancial(orderToCancel.totalAmount)}</span>
              </div>
            </div>
          )}
          
          <div className={styles.formGroup}>
            <label htmlFor="cancellationReason" className={styles.formLabel}>
              {t('admin.orders.cancellationReason', 'Motivo de Cancelación')}
              <span className={styles.required}>*</span>
            </label>
            <textarea
              id="cancellationReason"
              className={styles.textArea}
              value={cancellationReason}
              onChange={(e) => setCancellationReason(e.target.value)}
              placeholder={t('admin.orders.enterCancellationReason', 'Ingresa el motivo de cancelación')}
              required
            />
            {cancelError && <div className={styles.errorMessage}>{cancelError}</div>}
          </div>
        </div>
        
        <div className={styles.modalFooter}>
          <button 
            className={styles.cancelButton} 
            onClick={handleCloseCancelModal}
            disabled={isCancelling}
          >
            {t('common.cancel', 'Cancelar')}
          </button>
          <button 
            className={styles.confirmButton} 
            onClick={handleConfirmCancel}
            disabled={isCancelling}
          >
            {isCancelling ? t('admin.orders.processing', 'Procesando...') : t('admin.orders.confirmCancel', 'Confirmar Cancelación')}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <CommonLayout head={renderHead} body={renderBody} />
      {showCancelModal && renderCancelModal}
    </>
  );
};

export default OrdersList;
